import { useEffect, useState } from 'react';
import Navbar from './Navbar';

export default function HeroSection() {
  const [textIndex, setTextIndex] = useState(0);
  const text = "Tasarım. Üretim. Rekabet.";

  useEffect(() => {
    if (textIndex < text.length) {
      const timeout = setTimeout(() => {
        setTextIndex(textIndex + 1);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [textIndex, text.length]);

  return (
    <section className="relative min-h-screen w-full overflow-hidden bg-black">
      {/* Background Video/Animation Layer */}
      <div className="absolute inset-0">
        <img 
          src="https://readdy.ai/api/search-image?query=dark%20modern%20engineering%20workshop%20with%20racing%20car%20parts%20carbon%20fiber%20materials%20dramatic%20lighting%20industrial%20atmosphere%20high%20tech%20manufacturing%20facility%20motorsport%20garage%20professional%20equipment%20tools%20and%20machinery%20cinematic%20moody%20blue%20accent%20lights&width=1920&height=1080&seq=hero-bg-1&orientation=landscape"
          alt="Engineering Workshop"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80" />
      </div>

      {/* Navbar */}
      <Navbar />

      {/* Hero Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-8">
        <div className="max-w-7xl w-full mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="text-left space-y-8">
            {/* Main Title with Staggered Animation */}
            <div className="overflow-hidden">
              <h1 className="text-[120px] font-black leading-none tracking-tight">
                {['T', 'E', 'T', 'R', 'A'].map((letter, i) => (
                  <span
                    key={i}
                    className="inline-block animate-slide-in bg-gradient-to-br from-white via-gray-200 to-[#00d9ff] bg-clip-text text-transparent"
                    style={{
                      animationDelay: `${i * 0.1}s`,
                      textShadow: '0 0 40px rgba(0,217,255,0.3)'
                    }}
                  >
                    {letter}
                  </span>
                ))}
              </h1>
            </div>

            {/* Subtitle */}
            <div className="animate-fade-up" style={{ animationDelay: '0.5s' }}>
              <h2 className="text-5xl font-light text-white/90 tracking-wide">
                Engineering Team
              </h2>
            </div>

            {/* Description with Typewriter */}
            <div className="animate-fade-up" style={{ animationDelay: '1s' }}>
              <p className="text-2xl text-white/80 tracking-[0.15em] font-light">
                {text.substring(0, textIndex)}
                <span className="animate-blink">|</span>
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex gap-5 pt-8 animate-fade-up" style={{ animationDelay: '1.5s' }}>
              <button className="group relative px-10 py-4 bg-[#00d9ff] text-black font-semibold rounded-full overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(0,217,255,0.6)] whitespace-nowrap cursor-pointer">
                <span className="relative z-10 flex items-center gap-3">
                  Takıma Katıl
                  <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-2" />
                </span>
              </button>

              <button className="group relative px-10 py-4 bg-transparent border-2 border-[#ff6b35] text-white font-semibold rounded-full overflow-hidden transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer">
                <span className="absolute inset-0 bg-[#ff6b35] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                <span className="relative z-10 group-hover:text-black transition-colors duration-300">
                  Projelerimiz
                </span>
              </button>
            </div>
          </div>

          {/* Right - 3D Car Model Placeholder */}
          <div className="relative animate-fade-up" style={{ animationDelay: '0.8s' }}>
            <div className="relative group cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=formula%20student%20racing%20car%203d%20render%20side%20view%20carbon%20fiber%20body%20electric%20vehicle%20modern%20design%20sleek%20aerodynamic%20professional%20studio%20lighting%20dark%20background%20blue%20accent%20lights%20high%20detail%20motorsport%20engineering&width=800&height=600&seq=hero-car-1&orientation=landscape"
                alt="TETRA Racing Car"
                className="w-full h-auto object-contain transform transition-all duration-700 group-hover:scale-110"
                style={{
                  filter: 'drop-shadow(0 0 50px rgba(0,217,255,0.4))'
                }}
              />
              
              {/* Tech Detail Points */}
              <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-[#00d9ff] rounded-full animate-pulse opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute inset-0 bg-[#00d9ff] rounded-full animate-ping" />
              </div>
              <div className="absolute top-1/2 right-1/4 w-3 h-3 bg-[#ff6b35] rounded-full animate-pulse opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ animationDelay: '0.2s' }}>
                <div className="absolute inset-0 bg-[#ff6b35] rounded-full animate-ping" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 animate-bounce cursor-pointer">
        <div className="text-white/60 text-xs uppercase tracking-[0.3em]">Scroll to Explore</div>
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-[#00d9ff] rounded-full animate-scroll" />
        </div>
      </div>

      <style>{`
        @keyframes slide-in {
          from { transform: translateY(100px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fade-up {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        @keyframes scroll {
          0% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(16px); opacity: 0; }
        }
        .animate-slide-in {
          animation: slide-in 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          opacity: 0;
        }
        .animate-fade-up {
          animation: fade-up 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          opacity: 0;
        }
        .animate-blink {
          animation: blink 1s infinite;
        }
        .animate-scroll {
          animation: scroll 1.5s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}
